import type { ReactNode } from "react";
import type { MascotPose } from "../lib/mascot";
import "./Mascot.css";

/**
 * The host, drawn by hand as one SVG rig: a shuttlecock with the cork for a head and the feathers
 * for hair, and a gold monocle on its right eye on a chain to a button on the cork (decision 44).
 * Poses swap the face, the arms, the extras and where the monocle sits; the body never changes.
 * Every part that moves has a class of its own (`m-arm-r`, `m-eyes`, `m-lens` and so on) and the
 * movement is in motion.css, keyed on `data-pose`: nothing here runs a timer. He is Lord
 * Cockington, in charge for the day (lib/mascot.ts, decision 45). The accent and the gold come
 * from the tokens.
 *
 * The face is drawn back to front: the eyes and the mouth, the monocle over them, the brows over
 * the monocle's ring (they hold it in), the cork's band over all of it (a raised brow goes under
 * it), and the glint last. The ring and the brows sit on a paper edge, so a line that passes
 * behind them stops short instead of running into their ink.
 */

const INK = "#111111";
/** The outline and the limbs follow the ground's ink, so the host still reads on a dark ground. */
const LINE = "var(--bd-ink)";
const PAPER = "#ffffff";
const ACCENT = "var(--bd-accent)";
/** The crown, the monocle's ring and its chain. */
const GOLD = "var(--bd-court-1)";
/** The lens: a light wash of the cyan, so the eye shows through and the glint shows on it. */
const LENS = "var(--bd-court-2)";

const line = { stroke: INK, strokeWidth: 5, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, fill: "none" };

/** The band round the top of the cork. */
const BAND = "M56 110h88v16H56z";

function Glove({ x, y, point = false, at = false }: { x: number; y: number; point?: boolean; at?: boolean }) {
  if (at) {
    // pointing out of the picture at whoever looks, as on the old recruiting posters: a fist in front
    // of him with its knuckles along the top, the finger coming straight at them, seen end on as a
    // round tip with its nail, and three short strokes round the tip for the jab
    return (
      <g className="m-jab">
        <rect x={x - 20} y={y - 16} width="40" height="32" rx="12" fill={PAPER} stroke={LINE} strokeWidth="6" />
        <path d={`M${x - 9} ${y - 16}v7M${x + 2} ${y - 16}v7M${x + 12} ${y - 15}v6`} fill="none" stroke={LINE} strokeWidth="3" strokeLinecap="round" />
        <circle cx={x} cy={y + 8} r="14" fill={PAPER} stroke={LINE} strokeWidth="5" />
        <path d={`M${x - 7} ${y + 3}q7-7 14 0v6q-7 4-14 0z`} fill="none" stroke={LINE} strokeWidth="3" strokeLinejoin="round" />
        <path d={`M${x + 26} ${y + 6}h9M${x + 20} ${y + 24}l7 6M${x} ${y + 30}v9`} fill="none" stroke={LINE} strokeWidth="4" strokeLinecap="round" />
      </g>
    );
  }
  return (
    <g>
      {point && <rect x={x - 5} y={y - 32} width="10" height="24" rx="5" fill={PAPER} stroke={LINE} strokeWidth="5" />}
      <circle cx={x} cy={y} r="11" fill={PAPER} stroke={LINE} strokeWidth="6" />
    </g>
  );
}

function Arm({ d }: { d: string }) {
  return <path d={d} fill="none" stroke={LINE} strokeWidth="6" strokeLinecap="round" />;
}

/** One arm: the line from the shoulder and the glove. Left and right are as seen on the screen. */
type Limb = { d: string; x: number; y: number; point?: boolean; at?: boolean };

const ARMS: Record<"calling" | "up" | "whistle" | "nag" | "down" | "point", [Limb, Limb]> = {
  calling: [{ d: "M52 150c-22 4-34-8-40-22", x: 12, y: 124 }, { d: "M148 150c20-2 30-18 30-38", x: 178, y: 106, point: true }],
  up: [{ d: "M54 146c-22-6-32-22-34-40", x: 20, y: 102 }, { d: "M146 146c22-6 32-22 34-40", x: 180, y: 102 }],
  whistle: [{ d: "M52 152c-20 8-34 4-42-8", x: 10, y: 142 }, { d: "M148 152c18 6 20 14 6 22", x: 150, y: 176 }],
  nag: [{ d: "M52 156c-16 10-22 22-16 34", x: 38, y: 192 }, { d: "M148 150c20-2 30-18 30-38", x: 178, y: 106, point: true }],
  down: [{ d: "M52 156c-16 10-22 22-16 34", x: 38, y: 192 }, { d: "M148 156c16 10 22 22 16 34", x: 162, y: 192 }],
  // a hand on his hip, and the other arm bent forward to point at the viewer
  point: [{ d: "M52 156c-16 10-22 22-16 34", x: 38, y: 192 }, { d: "M146 150c8 2 12 4 14 8", x: 170, y: 166, at: true }],
};

function armsFor(pose: MascotPose): [Limb, Limb] {
  if (pose === "calling") return ARMS.calling;
  if (pose === "crown" || pose === "shock") return ARMS.up;
  if (pose === "whistle") return ARMS.whistle;
  if (pose === "nag") return ARMS.nag;
  if (pose === "point") return ARMS.point;
  return ARMS.down;
}

/** The arms behind the body; an arm that points out at the viewer is drawn in front of it instead (`front`). */
function Arms({ pose, front = false }: { pose: MascotPose; front?: boolean }) {
  const [left, right] = armsFor(pose);
  const ahead = right.at === true;
  if (front) {
    return ahead ? (
      <g className="m-arm-r">
        <Arm d={right.d} />
        <Glove x={right.x} y={right.y} at />
      </g>
    ) : null;
  }
  return (
    <>
      <g className="m-arm-l">
        <Arm d={left.d} />
        <Glove x={left.x} y={left.y} point={left.point === true} />
      </g>
      {ahead ? null : (
        <g className="m-arm-r">
          <Arm d={right.d} />
          <Glove x={right.x} y={right.y} point={right.point === true} />
        </g>
      )}
    </>
  );
}

/** A four-pointed sparkle in the gold: the crown's, and the glint that flashes off the lens. */
function Spark({ x, y, s = 1, className }: { x: number; y: number; s?: number; className: string }) {
  const k = 7 * s;
  const w = 2 * s;
  return (
    <path
      className={`m-star ${className}`}
      d={`M${x} ${y - k}L${x + w} ${y - w}L${x + k} ${y}L${x + w} ${y + w}L${x} ${y + k}L${x - w} ${y + w}L${x - k} ${y}L${x - w} ${y - w}Z`}
      fill={GOLD}
      stroke={INK}
      strokeWidth="2"
      strokeLinejoin="round"
    />
  );
}

/**
 * A face, part by part, so each part can move on its own. `eye` draws one eye at an x. `brows` is
 * one path, the monocle's side first: that brow lies along the top of the ring, so none of the
 * ring shows above it. Mouths keep clear of the ring and its paper edge.
 */
interface FaceParts {
  eye: (cx: number) => ReactNode;
  brows?: string;
  mouth: ReactNode;
  extra?: ReactNode;
}

const dot = (cx: number) => <circle key={cx} cx={cx} cy="148" r="7" fill={INK} />;
const frown = "M77 132.8L95 136M128 134l-18 5";

function face(pose: MascotPose): FaceParts {
  switch (pose) {
    case "asleep":
      return {
        eye: (cx) => <path key={cx} d={`M${cx - 8} 148q8 6 16 0`} {...line} />,
        mouth: <ellipse cx="104" cy="172" rx="6" ry="5" fill={INK} />,
        extra: (
          <>
            <path className="m-z m-z1" d="M150 96h16l-16 16h16" {...line} strokeWidth="4" />
            <path className="m-z m-z2" d="M172 70h12l-12 12h12" {...line} strokeWidth="4" />
            <path className="m-z m-z3" d="M190 48h8l-8 8h8" {...line} strokeWidth="3.5" />
          </>
        ),
      };
    case "shock":
      return {
        eye: (cx) => (
          <g key={cx}>
            <circle cx={cx} cy="146" r="9" fill={PAPER} stroke={INK} strokeWidth="5" />
            <circle cx={cx} cy="146" r="3" fill={INK} />
          </g>
        ),
        mouth: <ellipse cx="101" cy="172" rx="9" ry="11" fill={INK} />,
      };
    case "whistle":
      return {
        eye: dot,
        brows: frown,
        mouth: <rect x="98" y="162" width="30" height="14" rx="6" fill={ACCENT} stroke={INK} strokeWidth="5" />,
        extra: <path className="m-sound" d="M140 157l10-6M142 169h12M140 181l10 6" {...line} strokeWidth="4" />,
      };
    case "nag":
      return { eye: dot, brows: "M75 133.5L95 133M130 138l-22-2", mouth: <path d="M88 172h26" {...line} /> };
    case "point":
      // stern and certain: the brows down to the middle, a firm mouth
      return { eye: dot, brows: "M75 131L95 136M131 133l-21 4", mouth: <path d="M90 171q14-5 28 0" {...line} /> };
    case "crown":
      return {
        eye: (cx) => <path key={cx} d={`M${cx - 8} 148q8-8 16 0`} {...line} />,
        mouth: <path d="M90 170q17 18 34 0z" fill={INK} stroke={INK} strokeWidth="5" strokeLinejoin="round" />,
        extra: (
          <>
            <Spark x={44} y={6} s={1.1} className="m-spark" />
            <Spark x={160} y={-2} s={0.9} className="m-spark m-spark2" />
          </>
        ),
      };
    case "smug":
      return {
        eye: (cx) => <path key={cx} d={`M${cx - 8} 148h16`} {...line} strokeWidth="6" />,
        brows: "M76 135.5Q85 132.5 94 135.5M128 132l-18 6",
        mouth: <path d="M93 169q16 11 27-4" {...line} />,
      };
    default:
      return { eye: dot, brows: frown, mouth: <path d="M92 169q15 13 28-2" {...line} /> };
  }
}

/** The brows, on a paper edge, so where one lies across the monocle's ring the ring goes behind it. */
function Brows({ d }: { d: string }) {
  return (
    <g className="m-brows">
      <path d={d} {...line} stroke={PAPER} strokeWidth="8" />
      <path d={d} {...line} />
    </g>
  );
}

/** The monocle's ring, and the button on the cork its chain hangs from. */
const RING = 13;
const BUTTON = { x: 62, y: 166 };

/** Where the monocle sits: on the right eye; slipped when asleep, tipped when smug, fallen out in shock. */
function monoclePlace(pose: MascotPose): { cx: number; cy: number; tilt: number; fallen: boolean } {
  if (pose === "shock") return { cx: 54, cy: 200, tilt: -8, fallen: true };
  if (pose === "asleep") return { cx: 86, cy: 153, tilt: 12, fallen: false };
  if (pose === "smug") return { cx: 84, cy: 148, tilt: -8, fallen: false };
  return { cx: 84, cy: 148, tilt: 0, fallen: false };
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/** The chain: gold links edged in ink, so it reads as a chain and not as a seam on the cork. */
function Chain({ d }: { d: string }) {
  const links = { strokeDasharray: "0.1 7", strokeLinecap: "round" as const, fill: "none" };
  return (
    <>
      <path d={d} {...links} stroke={INK} strokeWidth="5.5" />
      <path d={d} {...links} stroke={GOLD} strokeWidth="2.5" />
    </>
  );
}

/**
 * The monocle (decision 44): a gold ring edged in ink, a lens with a white glint, a chain of gold
 * links sagging to a gold button. Under the ring is a paper edge, under the chain, so the chain
 * still reaches the ring. The ring and its edge each sit in an `m-lens`, which the stylesheet moves
 * together; in shock the whole monocle hangs in `m-dangle`, which swings from the button.
 */
function Monocle({ pose }: { pose: MascotPose }) {
  const { cx, cy, tilt, fallen } = monoclePlace(pose);
  const r = RING;
  const edge = <circle cx={cx} cy={cy} r={r + 3} fill="none" stroke={PAPER} strokeWidth="6" />;
  const ring = (
    <g transform={`rotate(${tilt} ${cx} ${cy})`}>
      <circle cx={cx} cy={cy} r={r} fill={LENS} fillOpacity="0.24" />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={INK} strokeWidth="9" />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={GOLD} strokeWidth="4" />
      <path
        d={`M${round1(cx - r * 0.6)} ${round1(cy - r * 0.05)}A${round1(r * 0.62)} ${round1(r * 0.62)} 0 0 1 ${round1(cx - r * 0.05)} ${round1(cy - r * 0.6)}`}
        stroke={PAPER}
        strokeWidth="3.5"
        strokeLinecap="round"
        fill="none"
      />
    </g>
  );
  const button = <circle cx={BUTTON.x} cy={BUTTON.y} r="4" fill={GOLD} stroke={INK} strokeWidth="2.5" />;
  if (fallen) {
    return (
      <g className="m-mono">
        <g className="m-dangle">
          {edge}
          <Chain d={`M${BUTTON.x} ${BUTTON.y}C${BUTTON.x - 6} ${BUTTON.y + 10} ${cx + 4} ${cy - r - 10} ${cx} ${cy - r}`} />
          <g className="m-lens">
            {ring}
            <Spark x={round1(cx + r * 0.8)} y={round1(cy - r * 0.8)} s={0.9} className="m-flash" />
          </g>
        </g>
        {button}
      </g>
    );
  }
  // the chain leaves the ring just left of its bottom and sags in a loop up to the button
  const a = (100 * Math.PI) / 180;
  const sx = round1(cx + r * Math.cos(a));
  const sy = round1(cy + r * Math.sin(a));
  return (
    <g className="m-mono">
      <g className="m-lens">{edge}</g>
      <Chain d={`M${sx} ${sy}C${sx} ${round1(sy + 22)} ${BUTTON.x + 6} ${BUTTON.y + 24} ${BUTTON.x} ${BUTTON.y}`} />
      {button}
      <g className="m-lens">{ring}</g>
    </g>
  );
}

/** The glint off the lens, drawn last so no brow covers it. It moves with the lens (motion.css). */
function Glint({ pose }: { pose: MascotPose }) {
  const { cx, cy, fallen } = monoclePlace(pose);
  if (fallen) return null;
  return (
    <g className="m-glint">
      <Spark x={round1(cx + RING * 0.8)} y={round1(cy - RING * 0.8)} s={0.9} className="m-flash" />
    </g>
  );
}

export function Mascot({ pose = "calling", className }: { pose?: MascotPose; className?: string }) {
  const parts = face(pose);
  return (
    <svg className={className ? `mascot ${className}` : "mascot"} data-pose={pose} viewBox="0 -14 200 244" aria-hidden="true">
      <g stroke={LINE} strokeWidth="6" strokeLinejoin="round" strokeLinecap="round">
        {pose === "crown" && (
          <g className="m-crown">
            <path d="M58 30l10-38 16 22 16-26 16 26 16-22 10 38z" fill={GOLD} />
          </g>
        )}
        <path d="M28 34h144l-34 76H62z" fill={PAPER} />
        <path d="M64 34l12 76M100 34v76M136 34l-12 76M38 60h124M50 86h100" fill="none" stroke={INK} strokeWidth="4" />
        <Arms pose={pose} />
        <path d="M56 110h88v38a44 44 0 0 1-88 0z" fill={PAPER} />
        <path d="M84 194v22M116 194v22" fill="none" />
        <path d="M70 220h18M112 220h18" fill="none" strokeWidth="8" />
        <Arms pose={pose} front />
      </g>
      <g className="m-eyes">
        {parts.eye(84)}
        {parts.eye(118)}
      </g>
      <g className="m-mouth">{parts.mouth}</g>
      <Monocle pose={pose} />
      {parts.brows ? <Brows d={parts.brows} /> : null}
      <path d={BAND} fill={ACCENT} stroke={LINE} strokeWidth="6" strokeLinejoin="round" />
      <Glint pose={pose} />
      {parts.extra ?? null}
    </svg>
  );
}

/** The host with something to say. `side` is where the character stands relative to the bubble. */
export function MascotSays({
  pose,
  line: words,
  side = "right",
  className,
}: {
  pose: MascotPose;
  line: string;
  side?: "left" | "right";
  className?: string;
}) {
  return (
    <div className={`mascot-says mascot-says-${side}${className ? ` ${className}` : ""}`}>
      <p key={words} className="mascot-bubble">
        {words}
      </p>
      <Mascot pose={pose} />
    </div>
  );
}
